import type { ReactNode, Ref, SelectHTMLAttributes } from "react";
import { classNames } from "./internal/class-names";
import { resolveControlAria, useFieldBinding } from "./field";

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean | undefined;
}

type NativeSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "aria-describedby" | "aria-invalid"
>;

export interface SelectProps extends NativeSelectProps {
  /** Declarative options. Omit and pass `children` when option groups are needed. */
  readonly options?: readonly SelectOption[] | undefined;
  /** Rendered as a disabled, empty-valued first entry. */
  readonly placeholder?: string | undefined;
  readonly describedBy?: string | undefined;
  readonly invalid?: boolean | undefined;
  readonly ref?: Ref<HTMLSelectElement> | undefined;
}

/**
 * Native `<select>`. Deliberately not a custom listbox: the native control is
 * the most reliable keyboard and screen-reader experience and costs no bundle.
 */
export function Select({
  children,
  className,
  describedBy,
  invalid,
  options,
  placeholder,
  ...rest
}: SelectProps): ReactNode {
  const aria = resolveControlAria(useFieldBinding(), {
    describedBy,
    disabled: rest.disabled,
    id: rest.id,
    invalid,
    required: rest.required,
  });

  return (
    <select
      {...rest}
      aria-describedby={aria.describedBy}
      aria-invalid={aria.invalid}
      className={classNames("thw-control", "thw-select", className)}
      disabled={aria.disabled}
      id={aria.id}
      required={aria.required}
    >
      {placeholder === undefined ? null : (
        <option disabled value="">
          {placeholder}
        </option>
      )}
      {options?.map((option) => (
        <option disabled={option.disabled} key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
      {children}
    </select>
  );
}
